import { AddElementMenu } from "@/components/forms/components/AddElementMenu.tsx";
import { FormPageComponent } from "@/components/forms/components/FormPageComponent.tsx";
import { HighlightDroppable } from "@/components/forms/components/HighlightDroppable.tsx";
import { FormEditorTypes } from "@/components/forms/form-editor.config.ts";
import { useFormEditor } from "@/components/forms/form-editor-context.ts";
import { useFormDragDrop } from "@/components/forms/utils/useFormDragDrop.tsx";
import usePermissions from "@/hooks/use-permissions.hook.ts";
import { ApiPermission } from "@/types/role.types.ts";
import { DragDropContext } from "@hello-pangea/dnd";
import { Box, Button, EmptyStateBase, Stack } from "@skailer-com/uikit";
import { useTranslation } from "react-i18next";

export const FormEditorComponent = () => {
  const { formValues, formEditorType, readOnly, handleStartFromScratch, handleSaveAsTemplate } =
    useFormEditor();
  const { handleDragEnd } = useFormDragDrop();
  const { t } = useTranslation();

  const { canIEdit } = usePermissions();

  const canManageSurveyTemplates =
    canIEdit(ApiPermission.survey_templates) &&
    [FormEditorTypes.survey, FormEditorTypes.surveyTemplate].includes(formEditorType);

  const canManagePerformanceReviewTemplates =
    canIEdit(ApiPermission.performance_review_templates) &&
    [FormEditorTypes.performanceReview, FormEditorTypes.performanceReview].includes(formEditorType);

  const canManageTemplates = canManageSurveyTemplates || canManagePerformanceReviewTemplates;

  if (!formValues || !formValues.pages) {
    return null;
  }

  const totalElements = formValues.pages.reduce(
    (acc, page) =>
      acc + page.sections.reduce((secAcc, section) => secAcc + section.elements.length, 0),
    0
  );

  const showFormTemplateButtons = [
    FormEditorTypes.survey,
    FormEditorTypes.performanceReview,
  ].includes(formEditorType);

  function getButtonStartFromScratch() {
    return (
      <Button
        variant="secondary"
        onClick={() => {
          handleStartFromScratch();
        }}
      >
        {t("surveys:start_from_scratch") + "..."}
      </Button>
    );
  }

  return (
    <DragDropContext onDragEnd={handleDragEnd}>
      <HighlightDroppable droppableId="pages" type="page">
        {(provided) => (
          <Stack {...provided.droppableProps} ref={provided.innerRef} gap={"lg"}>
            {formValues.pages.map((_, pageIndex) => (
              <FormPageComponent key={`page-${pageIndex}`} pageIndex={pageIndex} />
            ))}
            {provided.placeholder}

            {totalElements == 0 ? (
              <EmptyStateBase
                emoji={":heavy_plus_sign:"}
                title={t("surveys:no_elements_in_survey_empty_state_title")}
                description={t("surveys:no_elements_in_survey_empty_state_description")}
                primaryButton={
                  !readOnly && (
                    <Stack direction={"horizontal"} gap={"md"}>
                      <AddElementMenu />
                      {showFormTemplateButtons && getButtonStartFromScratch()}
                    </Stack>
                  )
                }
              />
            ) : (
              !readOnly && (
                <Box className={"pl-34"}>
                  <Stack direction={"horizontal"} gap={"md"}>
                    <AddElementMenu />
                    {showFormTemplateButtons && (
                      <>
                        {getButtonStartFromScratch()}
                        {canManageTemplates && (
                          <Button
                            variant="secondary"
                            onClick={() => {
                              handleSaveAsTemplate();
                            }}
                          >
                            {t("surveys:save_as_template") + "..."}
                          </Button>
                        )}
                      </>
                    )}
                  </Stack>
                </Box>
              )
            )}
          </Stack>
        )}
      </HighlightDroppable>
    </DragDropContext>
  );
};
